/**
 * Instagram Media Service
 * Fetches paginated media (photos) from Instagram Graph API using the stored long-lived token.
 * Used by ProfileGalleryPage for "Import from Instagram" with Load more.
 *
 * @author Varun Pandey
 */

import axios from 'axios';
import { useInstagramStore } from '@/stores/instagramStore';

const GRAPH_API_BASE = 'https://graph.instagram.com';
const FIELDS = 'id,media_type,media_url,thumbnail_url,caption,timestamp';

export interface InstagramGraphMediaItem {
  id: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  media_url: string;
  thumbnail_url?: string;
  caption?: string;
  timestamp?: string;
}

export interface FetchInstagramMediaResult {
  media: InstagramGraphMediaItem[];
  nextCursor: string | null;
}

async function fetchRawMediaPage(
  limit: number,
  after?: string | null
): Promise<{ data: InstagramGraphMediaItem[]; nextCursor: string | null }> {
  const { connection } = useInstagramStore.getState();
  const token = connection?.accessToken;
  const userId = connection?.instagramId;

  if (!token || !userId) {
    throw new Error('Instagram is not connected. Please connect your account first.');
  }

  const url = `${GRAPH_API_BASE}/v18.0/${userId}/media`;
  const params: Record<string, string> = {
    fields: FIELDS,
    access_token: token,
    limit: String(limit),
  };
  if (after) {
    params.after = after;
  }

  const response = await axios.get<{
    data: InstagramGraphMediaItem[];
    paging?: {
      cursors?: { before?: string; after?: string };
      next?: string;
    };
  }>(url, { params });

  const data = response.data?.data ?? [];
  const afterCursor = response.data?.paging?.cursors?.after ?? null;

  return {
    data,
    nextCursor: afterCursor || null,
  };
}

/**
 * Fetch one page of Instagram media (images only) using Graph API.
 * Token and user id come from instagramStore (set after ARTIST_INSTAGRAM_CONNECT).
 */
export async function fetchInstagramMediaPage(
  limit: number = 20,
  after?: string | null
): Promise<FetchInstagramMediaResult> {
  const { data, nextCursor } = await fetchRawMediaPage(limit, after);
  const imageMedia = data.filter(
    (m) => m.media_type === 'IMAGE' || m.media_type === 'CAROUSEL_ALBUM'
  );

  return {
    media: imageMedia,
    nextCursor,
  };
}

/**
 * Fetch one page of Instagram media (videos only) using Graph API.
 * Useful for populating the Videos tab in the gallery directly from Instagram.
 */
export async function fetchInstagramVideosPage(
  limit: number = 20,
  after?: string | null
): Promise<FetchInstagramMediaResult> {
  const { data, nextCursor } = await fetchRawMediaPage(limit, after);
  const videoMedia = data.filter((m) => m.media_type === 'VIDEO');

  return {
    media: videoMedia,
    nextCursor,
  };
}
